"use strict"

import "./VehicleProperties.css";
import React from "react";
import classnames from "classnames";

import dispatcher from "./../../dispatcher";
import vehicleStore from "./../../store/vehicles";
import vehicleModificationStore from "./../../store/vehicleModifications";
import sessionStore from "./../../store/session";

import createWellQuantity from "vwa/quantity/createWellQuantity";
import getOriginalValueFromWellQuantity from "vwa/quantity/getOriginalValueFromWellQuantity";
import format from "vwa/quantity/format";
import {getUnitAssignment} from "./../../utils/user";

import Form from "vwa/components/Form";
import ProgressBar from "vwa/components/ProgressBar";
import Card from "vwa/components/Card";
import Button from "vwa/components/Button";
import Input from "vwa/components/Input";
import ColorPicker from "vwa/components/ColorPicker";
import InputWithUnit from "vwa/components/InputWithUnit";
import Toast from "vwa/components/Toast";
import lang from "vwa/lang";

import EEP from "vfu/EEP";
import shouldComponentUpdate from "vwa/shouldComponentUpdate";

import {getPrivilegeOfVehicle} from "./../../utils/vehicle";
import saveVehicle from "./../../api/saveVehicle";

const DEFAULT_COLOR = "#FFFFFF";

const propertyToQuantity = {
	weight:		"weight",
	height:		"vehicleLength",
	length:		"vehicleLength",
	width:		"vehicleLength"
};

const defaultUnits = {
	weight:			"kg",
	vehicleLength:	"cm"
};

export default class VehicleProperties extends React.Component{
	
	constructor(props){
		super(props);
		this.initState();
		this.eep = new EEP();
		this.setStoreListeners();
	}
	
	initState(){
		this.state = {
			inProgress: false,
			vehicle: vehicleStore.get(this.props.vehicleId),
			modifications: this.getModificationsFromStore()
		};
	}
	
	shouldComponentUpdate(...nexts){
		return shouldComponentUpdate(this, ...nexts);
	}
	
	getModificationsFromStore() {
		const modDoc = vehicleModificationStore.get(this.props.vehicleId)
		if (!modDoc)
			return {};
		return modDoc.modifications;
	}
	
	setStoreListeners() {
		this.eep.on(vehicleStore, "update", vehicle => {
			if (vehicle._id == this.props.vehicleId)
				this.setState({vehicle});
		});
		this.eep.on(vehicleModificationStore, "insert", modificationDocs => {
			const modificationDoc = modificationDocs.find(md => md.vehicle == this.props.vehicleId);
			if (!modificationDoc)
				return;
			this.setState({
				modifications: this.getModificationsFromStore()
			});
		});
		this.eep.on(vehicleModificationStore, "update", modificationDoc => {
			if (modificationDoc.vehicle == this.props.vehicleId)
			this.setState({
				modifications: this.getModificationsFromStore()
			});
		});
		this.eep.on(vehicleModificationStore, "remove", modificationDocs => {
			const modificationDoc = modificationDocs.find(md => md.vehicle == this.props.vehicleId);
			if (!modificationDoc)
				return;
			this.setState({
				modifications: this.getModificationsFromStore()
			});
		});
	}
	
	componentWillUnmount() {
		this.eep.close();
	}
	
	hasModifications() {
		return Object.keys(this.state.modifications).length > 0;
	}
	
	getPropertyValue(field) {
		let value = null;
		if (field in this.state.modifications)
			return this.state.modifications[field];
		else return this.state.vehicle[field];
	}
	
	getWellQuantity(field) {
		const value = this.getPropertyValue(field);
		const unitAssignment = getUnitAssignment();
		if (!unitAssignment) return {value};
		const quantity = propertyToQuantity[field];
		if (!quantity) return {value};
		const defaultUnit = defaultUnits[quantity];
		return createWellQuantity(unitAssignment, quantity, value, defaultUnit, "integer") || {value};
	}
	
	getValue(field) {
		return this.getWellQuantity(field).value || "";
	}
	
	getUnit(field) {
		return this.getWellQuantity(field).unit || "";
	}
	
	canEdit() {
		const privilege = getPrivilegeOfVehicle(this.props.vehicleId);
		return privilege >= 2;
	}
	
	handleQuantityPropertyChange(field, value) {
		const hasUnit = !!this.getUnit(field);
		const originalValue = originalValue ?
								getOriginalValueFromWellQuantity(
									{value, unit: this.getUnit(field)},
									defaultUnits[propertyToQuantity[field]],
								"integer") :
								format(value)
								
		const modifications = {
			[field]: originalValue
		};
		
		dispatcher.dispatch("modifyVehicle", this.props.vehicleId, modifications);
	}
	
	handlePropertyChange(field, value){
		dispatcher.dispatch("modifyVehicle", this.props.vehicleId, {[field]: value});
	}
	
	getSaveButtonDisabled() {
		return !this.canEdit() || !this.hasModifications();
	}
	
	async save() {
		this.setState({inProgress: true});
		await saveVehicle(this.props.vehicleId);
		this.setState({inProgress: false});
	}
	
	render(){
		return(
			<div className="VehicleProperties">
				<header className="Shadowed">
					<Button
						className="Red"
						onClick={()=>this.save()}
						disabled={this.getSaveButtonDisabled()}
					>{lang.SAVE}</Button>
				</header>
				<ProgressBar
					indeterminate
					shown={this.state.inProgress}
				/>
				<div className={classnames("Content", {"Hidden": this.state.inProgress})}>
					<Card>
						<Form>
							<label>{lang.NAME}</label>
							<Input
								value={this.getValue("name")}
								onChange={evt => this.handlePropertyChange("name", evt.target.value)}
								disabled={!this.canEdit()}
							/>
							
							<label>{lang.PLATE_NUMBER}</label>
							<Input
								value={this.getValue("plateNumber")}
								onChange={evt => this.handlePropertyChange("plateNumber", evt.target.value)}
								disabled={!this.canEdit()}
							/>
							
							<label>{lang.VIN}</label>
							<Input
								value={this.getValue("vin")} 
								onChange={evt => this.handlePropertyChange("vin", evt.target.value)}
								disabled={!this.canEdit()}
							/>
							
							<label>{lang.MAKE}</label>
							<Input
								value={this.getValue("make")}
								onChange={evt => this.handlePropertyChange("make", evt.target.value)}
								disabled={!this.canEdit()}
							/>
							
							<label>{lang.YEAR_OF_PRODUCTION}</label>
							<Input
								value={this.getValue("yearOfProduction")}
								type="number"
								onChange={evt => this.handlePropertyChange("yearOfProduction", parseInt(evt.target.value))}
								min= "1900"
								max={(new Date()).getFullYear()}
								disabled={!this.canEdit()}
							/>
						</Form>
					</Card>
					
					<Card>
						<Form>
							<label>{lang.WEIGHT}</label>
							<InputWithUnit
								value={this.getValue("weight")}
								type="number"
								onChange={evt => this.handleQuantityPropertyChange("weight", evt.target.value)}
								label={this.getUnit("weight")}
								disabled={!this.canEdit()}
							/>
							
							<label>{lang.HEIGHT}</label>
							<InputWithUnit
								value={this.getValue("height")}
								type="number"
								onChange={evt => this.handleQuantityPropertyChange("height", evt.target.value)}
								label={this.getUnit("height")}
								disabled={!this.canEdit()}
							/>
							
							<label>{lang.LENGTH}</label>
							<InputWithUnit
								value={this.getValue("length")}
								type="number"
								onChange={evt => this.handleQuantityPropertyChange("length", evt.target.value)}
								label={this.getUnit("length")}
								disabled={!this.canEdit()}
							/>
							
							<label>{lang.WIDTH}</label>
							<InputWithUnit
								value={this.getValue("width")}
								type="number"
								onChange={evt => this.handleQuantityPropertyChange("width", evt.target.value)}
								label={this.getUnit("width")}
								disabled={!this.canEdit()}
							/>
							
							<label>{lang.COLOR}</label>
							<ColorPicker
								color={this.getValue("color") || DEFAULT_COLOR}
								onChange={ color => this.handlePropertyChange("color", color) }
								disabled={!this.canEdit()}
							/>
							
							<label>{lang.AXLE_NUMBER}:</label>
							<Input
								value={this.getValue("axleNumber")}
								type="number"
								onChange={evt => this.handlePropertyChange("axleNumber", parseInt(evt.target.value)) }
								disabled={!this.canEdit()}
							/>
						</Form>
					</Card>
				</div>
			</div>
		)
	}
	
}

VehicleProperties.propTypes = {
	vehicleId: React.PropTypes.string
};

VehicleProperties.defaultProps = {
	vehicleId: null
};



// WEBPACK FOOTER //
// ./src/view/VehicleProperties/VehicleProperties.jsx